import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
    closeModalDecision,
    pushState,
    readPushMarker,
    stepBackDecision,
    type IteroHistoryStep
} from './iteroBrowserHistory';
import {
    NO_SELECTION,
    hasIteroParams,
    isCanonicalIteroRoute,
    iteroRouteView,
    parseIteroBrowserRoute,
    writeIteroBrowserRoute,
    type IteroBrowserRoute
} from './iteroBrowserRoute';

export interface IteroBrowserRouteState {
    route: IteroBrowserRoute | null;
    hasParams: boolean;
    isCanonical: boolean;
    open: () => void;
    push: (next: IteroBrowserRoute) => void;
    // Replace keeps location.state, so a later close can still pop the entries we pushed.
    replace: (next: IteroBrowserRoute | null) => void;
    stepBack: (parent: IteroBrowserRoute) => void;
    close: () => void;
}

const OPEN_ROUTE: IteroBrowserRoute = { tab: 'patients', selection: NO_SELECTION };

// The URL is the source of truth for the browser's navigation; see iteroBrowserRoute.ts for the params.
export function useIteroBrowserRoute(): IteroBrowserRouteState {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const route = useMemo(() => parseIteroBrowserRoute(searchParams), [searchParams]);
    const marker = useMemo(() => readPushMarker(location.state), [location.state]);
    const currentView = iteroRouteView(route);
    const isOpen = route !== null;

    const push = useCallback(
        (next: IteroBrowserRoute) => {
            const state = pushState(marker, isOpen, iteroRouteView(next));
            setSearchParams((prev) => writeIteroBrowserRoute(prev, next), { state });
        },
        [marker, isOpen, setSearchParams]
    );

    const replace = useCallback(
        (next: IteroBrowserRoute | null) => {
            setSearchParams((prev) => writeIteroBrowserRoute(prev, next), {
                replace: true,
                state: location.state
            });
        },
        [location.state, setSearchParams]
    );

    const apply = useCallback(
        (step: IteroHistoryStep, fallback: IteroBrowserRoute | null) => {
            if (step.kind === 'pop') navigate(step.delta);
            else replace(fallback);
        },
        [navigate, replace]
    );

    const stepBack = useCallback(
        (parent: IteroBrowserRoute) => apply(stepBackDecision(marker, currentView), parent),
        [apply, marker, currentView]
    );
    const close = useCallback(() => apply(closeModalDecision(marker), null), [apply, marker]);
    const open = useCallback(() => {
        if (!isOpen) push(OPEN_ROUTE);
    }, [isOpen, push]);

    return {
        route,
        hasParams: hasIteroParams(searchParams),
        isCanonical: isCanonicalIteroRoute(searchParams),
        open,
        push,
        replace,
        stepBack,
        close
    };
}
